import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

interface BackLinkProps {
  text: string;
  href: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  'aria-label'?: string;
}

const BackLink: React.FC<BackLinkProps> = ({ 
  text, 
  href, 
  icon = <ArrowLeft size={16} />,
  onClick,
  className,
  'aria-label': ariaLabel
}) => {
  const [isHovered, setIsHovered] = React.useState<boolean>(false);

  const containerStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    textDecoration: 'none',
    cursor: 'pointer',
    width: '192px',
    color: isHovered ? '#000000' : 'var(--tailwind-colors-gray-900, #111827)',
    fontFamily: 'var(--font-font-sans, Inter)',
    fontSize: 'var(--text-sm-font-size, 14px)',
    fontStyle: 'normal',
    fontWeight: 'var(--font-weight-medium, 500)' as const,
    lineHeight: '1.4',
    transition: 'color 0.2s ease',
    ...(isHovered && {
      textDecoration: 'underline',
    }),
  };

  const iconStyles: React.CSSProperties = {
    flexShrink: 0,
    transform: isHovered ? 'translateX(-6px)' : 'translateX(0)',
    transition: 'transform 0.2s ease',
    display: 'flex',
    alignItems: 'center',
  };

  const textStyles: React.CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const handleClick = React.useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  }, [onClick]);

  const handleMouseEnter = React.useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    setIsHovered(false);
  }, []);

  return (
    <Link
      to={href}
      style={containerStyles}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      aria-label={ariaLabel || `Navigate back to ${text}`}
      role="button"
    >
      <span style={iconStyles}>
        {icon}
      </span>
      <span style={textStyles}>
        {text}
      </span>
    </Link>
  );
};

export default BackLink;